import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  getProject, 
  createProject,
  updateProject,
  assignProjectHumanAdmin,
  revokeProjectHumanAdmin
} from '../../../services/projectService';

const useProjectData = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  // Enhanced parameter detection with fallback
  const currentPath = window.location.pathname;
  const urlId = id || (currentPath.endsWith('/projects/new') ? 'new' : null);
  
  // Improved new project detection
  const isNewProject = urlId === 'new' || currentPath.endsWith('/projects/new');
  
  // Store the project ID for existing projects
  const projectId = isNewProject ? null : urlId;
  
  const [project, setProject] = useState({
    name: '',
    display_name: '',
    activity_description: '',
    keywords: [],
    primary_keywords: [],
    secondary_keywords: [],
    minus_words: [],
    bot_token: '',
    report_group_id: '',
    brand_analytics_theme_ids: [],
    brand_analytics_auto_join: false,
    human_admin_user_id: '',
    is_active: true
  });
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [assigningHumanAdmin, setAssigningHumanAdmin] = useState(false);
  const [revokingHumanAdmin, setRevokingHumanAdmin] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  useEffect(() => {
    if (!isNewProject && projectId) {
      fetchProjectData();
    } else {
      setLoading(false);
    }
  }, [urlId, isNewProject, projectId]);
  
  const fetchProjectData = async () => {
    if (!projectId) {
      setError('Invalid project ID');
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      
      // Fetch only project details (no groups)
      const projectData = await getProject(projectId);
      setProject(projectData);
      
      setError(null);
    } catch (err) {
      setError('Failed to load project data. Please try again later.');
    } finally {
      setLoading(false);
    }
  };
  
  const handleSave = async () => {
    const currentPath = window.location.pathname;
    const isCreatingProject = isNewProject || currentPath.endsWith('/projects/new');

    // Enhanced validation for both keyword systems
    if (!project.name || !project.bot_token || !project.report_group_id) {
      setError('Please fill in all required fields.');
      return;
    }
    
    // Check if at least one keyword system has keywords
    const hasLegacyKeywords = project.keywords && Array.isArray(project.keywords) && project.keywords.length > 0;
    const hasPrimaryKeywords = project.primary_keywords && Array.isArray(project.primary_keywords) && project.primary_keywords.length > 0;
    
    if (isCreatingProject && !hasLegacyKeywords && !hasPrimaryKeywords) {
      setError('Please add at least one keyword in either Legacy Keywords or Primary Keywords.');
      return;
    }
    
    try {
      setSaving(true);
      setError(null);

      const rawThemeIds = Array.isArray(project.brand_analytics_theme_ids)
        ? project.brand_analytics_theme_ids
        : String(project.brand_analytics_theme_ids || '').split(',');
      const brandAnalyticsThemeIds = [...new Set(
        rawThemeIds.map((value) => String(value).trim()).filter(Boolean)
      )];
      if (brandAnalyticsThemeIds.some((value) => !/^[1-9]\d*$/.test(value))) {
        setError('Brand Analytics theme IDs must be positive integers separated by commas.');
        return;
      }
      
      // Create clean project object without chats to reduce payload size
      const projectToSave = {
        name: project.name,
        display_name: project.display_name || project.name,
        activity_description: project.activity_description || '',
        keywords: project.keywords,
        primary_keywords: project.primary_keywords,
        secondary_keywords: project.secondary_keywords,
        minus_words: project.minus_words,
        bot_token: project.bot_token,
        report_group_id: project.report_group_id,
        brand_analytics_theme_ids: brandAnalyticsThemeIds,
        brand_analytics_auto_join: project.brand_analytics_auto_join === true,
        is_active: project.is_active
        // Exclude 'chats' field which can contain hundreds of groups and make request too large
      };
      
      if (isCreatingProject) {
        const newProject = await createProject(projectToSave);
        setSuccess('Project created successfully!');
        // Redirect to the new project's page
        setTimeout(() => {
          navigate(`/projects/${newProject.id}`);
        }, 1500);
      } else {
        // Verify we have a valid ID before updating
        if (!projectId) {
          throw new Error('Cannot update project: Invalid project ID');
        }
        // Use updateProject for existing projects with cleaned data
        const updatedProject = await updateProject(projectId, projectToSave);
        setProject(updatedProject);
        setSuccess('Project updated successfully!');
      }
    } catch (err) {
      setError(`Failed to save project: ${err.message || 'Unknown error'}`);
    } finally {
      setSaving(false);
    }
  };

  const handleAssignHumanAdmin = async () => {
    const rawUserId = String(project.human_admin_user_id || '').trim();
    if (!/^[1-9]\d*$/.test(rawUserId)) {
      setError('Enter a positive numeric Telegram user ID.');
      return;
    }

    const userId = Number(rawUserId);
    if (!Number.isSafeInteger(userId)) {
      setError('Telegram user ID is outside the supported numeric range.');
      return;
    }
    if (!projectId) {
      setError('Save the project before assigning a human administrator.');
      return;
    }

    try {
      setAssigningHumanAdmin(true);
      setError(null);
      setSuccess(null);
      const response = await assignProjectHumanAdmin(projectId, userId);
      const result = response && response.data;
      setProject((current) => ({
        ...current,
        ...(result && result.project ? result.project : {}),
        human_admin_user_id: result && result.humanAdminUserId
          ? result.humanAdminUserId
          : userId,
      }));
      setSuccess(
        result && result.alreadyAdministrator
          ? 'This user is already an administrator with the required rights.'
          : 'Human administrator assigned successfully.'
      );
    } catch (err) {
      const payload = err.response && err.response.data;
      const message = payload && (payload.error || payload.message);
      setError(message || 'Failed to assign human administrator. Check the user ID and group membership.');
    } finally {
      setAssigningHumanAdmin(false);
    }
  };

  const handleRevokeHumanAdmin = async () => {
    if (!projectId || !project.human_admin_user_id) {
      return;
    }
    if (!window.confirm(
      'Revoke this managed assignment? TeleFarm will restore the Telegram status captured before assignment.'
    )) {
      return;
    }
    try {
      setRevokingHumanAdmin(true);
      setError(null);
      setSuccess(null);
      const response = await revokeProjectHumanAdmin(projectId);
      const result = response && response.data;
      setProject((current) => ({
        ...current,
        ...(result && result.project ? result.project : {}),
        human_admin_user_id: '',
      }));
      setSuccess(
        result && result.alreadyRevoked
          ? 'No managed human administrator is currently assigned.'
          : result && result.preservedExistingAdministrator
            ? 'Managed assignment removed. The user kept administrator rights they had before TeleFarm management.'
            : 'Managed human administrator revoked and prior Telegram status restored.'
      );
    } catch (err) {
      const payload = err.response && err.response.data;
      const message = payload && (payload.error || payload.message);
      setError(message || 'Failed to revoke the managed human administrator.');
    } finally {
      setRevokingHumanAdmin(false);
    }
  };
  
  return {
    project,
    setProject,
    loading,
    saving,
    assigningHumanAdmin,
    revokingHumanAdmin,
    error,
    success,
    setError,
    setSuccess,
    isNewProject,
    projectId,
    handleSave,
    handleAssignHumanAdmin,
    handleRevokeHumanAdmin
  };
};

export default useProjectData;
